import React, { useState } from "react";
import useUnsavedChanges from '../../../hooks/useUnsavedChanges';
import UnsavedChangesDialog from '../../../components/UnsavedChangesDialog';
import CustomerSelect from "../../invoices/components/CustomerSelect";
import ProductSelect from "../../invoices/components/ProductSelect";
import PaymentSection from "../../invoices/components/PaymentSection";
import jsPDF from "jspdf";
import { searchProducts } from "../../product/services/productService";
import { searchCustomers } from "../../customers/services/customerService";
import { addOffer } from "../services/offerService";
import { useTranslation } from "react-i18next";
import { toast } from 'react-toastify';
import * as documentNoteTemplateService from '../../companysettings/services/documentNoteTemplateService';
import { DocumentType, getDefaultTemplateId, getTemplateContent } from '../../../utils/documentNoteTemplateUtils';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Typography,
  Card,
  CardHeader,
  CardContent,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Chip,
  Paper,
  Grid,
  TextField,
  Container,
  Stack,
  LinearProgress,
  alpha,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import PreviewIcon from "@mui/icons-material/Preview";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import PersonIcon from "@mui/icons-material/Person";
import InventoryIcon from "@mui/icons-material/Inventory";
import NotesIcon from "@mui/icons-material/Notes";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import PaymentIcon from "@mui/icons-material/Payment";

const vatOptions = [
  { value: 0, label: "0%" },
  { value: 5, label: "5%" },
  { value: 21, label: "21%" },
];

const MAX_CUSTOM_TEXT_LENGTH = 1000;
const MAX_NOTES_LENGTH = 1000;

// ✅ Backend enum status options
const OFFER_STATUS_OPTIONS = [
  { value: "Draft", label: "Draft", color: "#9e9e9e" },
  { value: "Sent", label: "Sent", color: "#2196f3" },
  { value: "Accepted", label: "Accepted", color: "#4caf50" },
  { value: "Rejected", label: "Rejected", color: "#f44336" },
];

// ✅ Payment method options
const PAYMENT_METHOD_OPTIONS = [
  { value: "Cash", label: "Cash", icon: "💵" },
  { value: "Virement", label: "Virement", icon: "🏦" },
  { value: "Bancontact", label: "Bancontact", icon: "💳" },
];

function AddOfferPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [price, setPrice] = useState("");
  const [quantity, setQuantity] = useState(1);
  const [vat, setVat] = useState(21);
  const [offerProducts, setOfferProducts] = useState([]);
  const blocker = useUnsavedChanges(offerProducts.length > 0);
  const [offerPaidAmount, setOfferPaidAmount] = useState("");
  const [customText, setCustomText] = useState("");
  const [notes, setNotes] = useState("");
  const [offerNoteTemplates, setOfferNoteTemplates] = useState([]);
  const [selectedOfferNoteTemplate, setSelectedOfferNoteTemplate] = useState('');

  useEffect(() => {
    // Skip if templates already loaded
    if (offerNoteTemplates.length > 0) return;
    
    documentNoteTemplateService.getTemplatesByType('Offer')
      .then((data) => {
        const allTemplates = data?.payload || data || [];
        // Filter templates by DocumentType.Offer (4)
        const templates = allTemplates.filter(t => 
          t.DocumentType === 4 || // DocumentType.Offer
          t.DocumentType === 99   // DocumentType.All
        );
        setOfferNoteTemplates(templates);
        // Auto-select default or first template and populate notes
        if (templates.length > 0) {
          const defaultTemplate = templates.find(t => t.IsDefault) || templates[0];
          setSelectedOfferNoteTemplate(defaultTemplate.Id);
          const plainText = defaultTemplate.NoteContent?.replace(/<[^>]*>/g, '') || '';
          setNotes(plainText.slice(0, MAX_NOTES_LENGTH));
        }
      })
      .catch(() => {});
  }, []);

  const handleOfferTemplateSelect = (id) => {
    setSelectedOfferNoteTemplate(id);
    if (!id) return;
    const tmpl = offerNoteTemplates.find((t) => t.Id === id);
    if (tmpl) setCustomText(tmpl.NoteContent.replace(/<[^>]*>/g, '').slice(0, MAX_CUSTOM_TEXT_LENGTH));
  };
  const [discountType, setDiscountType] = useState("percentage");
  const [discountValue, setDiscountValue] = useState(0);
  const [offerStatus, setOfferStatus] = useState("Draft");
  const [paymentMethod, setPaymentMethod] = useState("Cash");
  const [offerDate] = useState(new Date().toISOString());

  // Offer number generation
  const [offerNumber] = useState(() => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    const random = String(Math.floor(Math.random() * 1000)).padStart(3, '0');
    return `OFF-${year}${month}${day}-${random}`;
  });

  // ✅ Calculate totals with correct order: Discount first, then VAT
  const totalExcl = offerProducts.reduce((sum, p) => sum + Number(p.TotalExcl), 0);
  
  // ✅ Apply discount on base amount (excl VAT)
  let discountAmount = 0;
  if (discountType === "percentage") {
    discountAmount = (totalExcl * (parseFloat(discountValue) || 0)) / 100;
  } else {
    discountAmount = parseFloat(discountValue) || 0;
  }
  
  // ✅ Calculate amount after discount (still excl VAT)
  const amountAfterDiscount = totalExcl - discountAmount;
  
  // ✅ Calculate VAT on the discounted amount
  const totalVAT = offerProducts.reduce((sum, p) => {
    const productTotal = Number(p.TotalExcl);
    const productDiscount = (productTotal / totalExcl) * discountAmount; // Proportional discount
    const productAfterDiscount = productTotal - productDiscount;
    return sum + (productAfterDiscount * Number(p.VAT)) / 100;
  }, 0);
  
  // ✅ Final offer total (discounted amount + VAT on discounted amount)
  const offerTotal = amountAfterDiscount + totalVAT;
  const paidAmount = parseFloat(offerPaidAmount) || 0;
  const dueAmount = offerTotal - paidAmount;

  const customTextLength = customText.length;
  const notesLength = notes.length;

  const handleAddProduct = () => {
    if (selectedProduct && quantity > 0 && price > 0) {
      setOfferProducts([
        ...offerProducts,
        {
          ...selectedProduct,
          Quantity: quantity,
          Price: price,
          VAT: vat,
          TotalExcl: (quantity * price).toFixed(2),
          _rowKey: Date.now() + Math.random(),
        },
      ]);
      setSelectedProduct(null);
      setQuantity(1);
      setPrice("");
      setVat(21);
    }
  };

  const handleRemoveProduct = (rowKey) => {
    setOfferProducts(offerProducts.filter((p) => p._rowKey !== rowKey));
  };

  const loadProductOptions = async (inputValue) => {
    if (!inputValue || inputValue.length < 3) return [];
    let paramsObj;
    if (/^\d+$/.test(inputValue)) {
      paramsObj = { id: inputValue, sku: inputValue };
    } else {
      paramsObj = { name: inputValue, sku: inputValue };
    }
    const data = await searchProducts(paramsObj);
    return data.payload.map((p) => ({
      value: p.Id,
      label: `${p.Name} (ID: ${p.Id})${p.SKU ? " - SKU: " + p.SKU : ""}`,
      ...p,
    }));
  };

  const loadCustomerOptions = async (inputValue) => {
    return await searchCustomers(inputValue);
  };

  const handleGeneratePDF = () => {
    const doc = new jsPDF({ format: "a4", unit: "px", orientation: "portrait" });
    doc.text(`Offer PDF Preview\nOffer Number: ${offerNumber}`, 20, 20);
    doc.save("offer.pdf");
  };

  const handleSubmit = async () => {
    if (!selectedCustomer) {
      toast.error("Please select a customer.");
      return;
    }
    if (offerProducts.length === 0) {
      toast.error("Please add at least one product.");
      return;
    }
    
    // ✅ Map all fields according to backend model
    const offerData = {
      CustomerId: selectedCustomer.Id || selectedCustomer.value,
      OfferNumber: offerNumber,
      OfferDate: offerDate,
      OfferTotal: offerTotal,
      OfferPaidAmount: paidAmount,
      OfferDueAmount: dueAmount,
      CustomText: customText,
      Notes: notes,
      Discount: discountAmount,
      PaymentMethod: paymentMethod,
      VATPercentage: vat,
      LineItems: offerProducts.map(p => ({
        Id: 0,
        ProductId: p.Id,
        Quantity: p.Quantity,
        UnitPrice: p.Price,
        VATPercent: p.VAT,
      })),
    };
    
    
    try {
      const response = await addOffer(offerData);
      
      
      // ✅ Check if the response indicates failure
      if (response?.status === "Failure" || response?.errorCode || response?.statusCode === 401) {
        if (response?.errorCode === 12 || response?.message === "Unauthorized") {
          toast.error("Unauthorized: You don't have permission to create offers. Please log in again.");
        } else {
          toast.error(response?.message || "Failed to create offer");
        }
        return;
      }
      
      // ✅ Check if response is successful
      if (response?.status === "Success" || response?.payload || response?.Id) {
        toast.success("Offer created successfully!");
        
        // Navigate to offer detail page if we have an ID
        const offerId = response?.payload?.Id || response?.Id;
        if (offerId) {
          navigate(`/sales/offers/view/${offerId}`);
        } else {
          navigate('/sales/offers');
        }
      } else {
        toast.error("Failed to create offer. Please try again.");
      }
    } catch (err) {
      console.error("❌ Error creating offer:", err);
      
      if (err?.response?.status === 401 || err?.response?.data?.errorCode === 12) {
        toast.error("Unauthorized: Please log in again.");
      } else if (err?.response?.data?.message) {
        toast.error(`Error: ${err.response.data.message}`);
      } else if (err.message) {
        toast.error(err.message);
      } else {
        toast.error("Failed to create offer. Please check your connection and try again.");
      }
    }
  };

  const handleProductSelect = (product) => {
    setSelectedProduct(product);
    setPrice(product?.SalePrice || "");
  };

  return (
    <Box sx={{
      background: 'linear-gradient(to bottom, #f8fafc 0%, #f1f5f9 100%)',
      minHeight: '100vh',
      pt: 3,
      pb: 8
    }}>
      <Container maxWidth="lg">
        <Card elevation={3} sx={{ borderRadius: '16px', overflow: 'hidden', mb: 4 }}>
          <CardHeader
            title={
              <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', justifyContent: 'space-between' }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Box sx={{ bgcolor: alpha("#1976d2", 0.15), p: 1.2, borderRadius: '12px', mr: 2 }}>
                    <ReceiptLongIcon sx={{ color: "#1976d2" }} fontSize="large" />
                  </Box>
                  <Typography variant="h5" fontWeight="bold" color="primary">
                    {t("offer_details") || "Offer Details"}
                  </Typography>
                  <Chip
                    label={offerNumber}
                    color="primary"
                    variant="outlined"
                    size="medium"
                    sx={{
                      fontSize: "1rem",
                      fontWeight: "bold",
                      height: 32,
                      px: 2,
                      borderRadius: '8px',
                      background: alpha("#1976d2", 0.08),
                      ml: 2
                    }}
                  />
                </Box>
              </Box>
            }
            sx={{
              background: alpha("#1976d2", 0.08),
              borderBottom: `1px solid ${alpha("#1976d2", 0.15)}`
            }}
          />

          <CardContent>
            <Grid container spacing={3}>
              <Grid item xs={12} md={8}>
                <Stack spacing={3}>
                  {/* Customer Section */}
                  <Paper elevation={1} sx={{ p: 2, borderRadius: '12px', border: `1px solid ${alpha("#1976d2", 0.2)}` }}>
                    <Stack direction="row" alignItems="center" spacing={2} mb={1}>
                      <Box sx={{ bgcolor: alpha("#1976d2", 0.15), p: 1, borderRadius: '10px' }}>
                        <PersonIcon sx={{ color: "#1976d2" }} />
                      </Box>
                      <Typography variant="subtitle1" fontWeight="bold" color="primary">
                        {t("customer")}
                      </Typography>
                    </Stack>
                    <Box sx={{ mt: 1 }}>
                      <CustomerSelect
                        value={selectedCustomer}
                        onChange={setSelectedCustomer}
                        loadOptions={loadCustomerOptions}
                      />
                    </Box>
                  </Paper>

                  {/* Product Section */}
                  <Paper elevation={1} sx={{ p: 2, borderRadius: '12px', border: `1px solid ${alpha("#1976d2", 0.2)}` }}>
                    <Stack direction="row" alignItems="center" spacing={2} mb={2}>
                      <Box sx={{ bgcolor: alpha("#1976d2", 0.15), p: 1, borderRadius: '10px' }}>
                        <InventoryIcon sx={{ color: "#1976d2" }} />
                      </Box>
                      <Typography variant="subtitle1" fontWeight="bold" color="primary">
                        {t("product")}
                      </Typography>
                    </Stack>
                    <ProductSelect
                      value={selectedProduct}
                      onChange={handleProductSelect}
                      loadOptions={loadProductOptions}
                      quantity={quantity}
                      setQuantity={setQuantity}
                      price={price}
                      setPrice={setPrice}
                      vat={vat}
                      setVat={setVat}
                      vatOptions={vatOptions}
                      onAdd={handleAddProduct}
                      disabled={!selectedProduct || quantity <= 0 || !price || price <= 0}
                    />
                  </Paper>

                  {/* Product List */}
                  {offerProducts.length > 0 && (
                    <Paper elevation={1} sx={{ borderRadius: '12px', border: `1px solid ${alpha("#388e3c", 0.3)}`, bgcolor: alpha("#388e3c", 0.05), overflow: 'hidden' }}>
                      <Stack direction="row" alignItems="center" spacing={2} p={2}>
                        <Box sx={{ bgcolor: alpha("#388e3c", 0.15), p: 1, borderRadius: '10px' }}>
                          <InventoryIcon sx={{ color: "#388e3c" }} />
                        </Box>
                        <Typography variant="subtitle1" fontWeight="bold" color="success.dark">
                          {t("products_in_offer") || "Products in Offer"}
                        </Typography>
                        <Chip
                          label={`${offerProducts.length} ${t("items") || "items"}`}
                          color="success"
                          size="small"
                          sx={{ borderRadius: '8px', fontWeight: 'bold' }}
                        />
                      </Stack>
                      <Box sx={{ overflowX: 'auto', width: '100%' }}>
                        <Table size="small" sx={{ minWidth: 650 }}>
                          <TableHead>
                            <TableRow sx={{ background: alpha("#388e3c", 0.2) }}>
                              <TableCell sx={{ fontWeight: "bold" }}>{t("product_name")}</TableCell>
                              <TableCell sx={{ fontWeight: "bold" }}>{t("quantity")}</TableCell>
                              <TableCell sx={{ fontWeight: "bold" }}>{t("price")}</TableCell>
                              <TableCell sx={{ fontWeight: "bold" }}>{t("vat")}</TableCell>
                              <TableCell sx={{ fontWeight: "bold" }}>{t("total_excl")}</TableCell>
                              <TableCell sx={{ fontWeight: "bold" }}>{t("actions")}</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {offerProducts.map((p) => (
                              <TableRow key={p._rowKey} hover>
                                <TableCell sx={{ py: 1 }}>
                                  <Typography fontWeight={500}>{p.Name}</Typography>
                                </TableCell>
                                <TableCell sx={{ py: 1 }}>
                                  <Chip label={p.Quantity} size="small" color="info" sx={{ borderRadius: '8px' }} />
                                </TableCell>
                                <TableCell sx={{ py: 1 }}>
                                  <Typography fontWeight={500}>€{p.Price}</Typography>
                                </TableCell>
                                <TableCell sx={{ py: 1 }}>
                                  <Chip label={`${p.VAT}%`} size="small" variant="outlined" sx={{ borderRadius: '8px' }} />
                                </TableCell>
                                <TableCell sx={{ py: 1 }}>
                                  <Typography fontWeight={600} color="success.main">€{p.TotalExcl}</Typography>
                                </TableCell>
                                <TableCell sx={{ py: 1 }}>
                                  <Button
                                    onClick={() => handleRemoveProduct(p._rowKey)}
                                    color="error"
                                    size="small"
                                    variant="outlined"
                                    sx={{ borderRadius: '8px', minWidth: 0, px: 1, fontSize: 12 }}
                                  >
                                    Remove
                                  </Button>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </Box>
                    </Paper>
                  )}

                  {/* Custom Text */}
                  <Paper elevation={1} sx={{ p: 2, borderRadius: '12px', border: `1px solid ${alpha("#1976d2", 0.2)}` }}>
                    <Stack direction="row" alignItems="center" spacing={2} mb={2}>
                      <Box sx={{ bgcolor: alpha("#1976d2", 0.15), p: 1, borderRadius: '10px' }}>
                        <NotesIcon sx={{ color: "#1976d2" }} />
                      </Box>
                      <Typography variant="subtitle1" fontWeight="bold" color="primary">
                        {t("custom_text")}
                      </Typography>
                    </Stack>
                    {offerNoteTemplates.length > 0 && (
                      <FormControl fullWidth size="small" sx={{ mb: 1.5 }}>
                        <InputLabel>{t('select_template') || 'Select template'}</InputLabel>
                        <Select
                          value={selectedOfferNoteTemplate}
                          label={t('select_template') || 'Select template'}
                          onChange={(e) => handleOfferTemplateSelect(e.target.value)}
                        >
                          <MenuItem value=""><em>{t('none') || 'None'}</em></MenuItem>
                          {offerNoteTemplates.map((tmpl) => (
                            <MenuItem key={tmpl.Id} value={tmpl.Id}>{tmpl.TemplateName}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    )}
                    <TextField
                      multiline
                      minRows={3}
                      value={customText}
                      onChange={e => {
                        if (e.target.value.length <= MAX_CUSTOM_TEXT_LENGTH) {
                          setCustomText(e.target.value);
                        }
                      }}
                      fullWidth
                      placeholder={`Additional text for the offer (max ${MAX_CUSTOM_TEXT_LENGTH} characters)`}
                    />
                    <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 2 }}>
                      <LinearProgress
                        variant="determinate"
                        value={(customTextLength / MAX_CUSTOM_TEXT_LENGTH) * 100}
                        sx={{ flexGrow: 1, height: 8, borderRadius: 4, bgcolor: alpha("#b0b6c7", 0.5) }}
                        color={customTextLength >= MAX_CUSTOM_TEXT_LENGTH ? "error" : "primary"}
                      />
                      <Typography
                        variant="caption"
                        color={customTextLength >= MAX_CUSTOM_TEXT_LENGTH ? "error" : "text.secondary"}
                        sx={{ fontWeight: 'medium', whiteSpace: 'nowrap' }}
                      >
                        {customTextLength} / {MAX_CUSTOM_TEXT_LENGTH}
                      </Typography>
                    </Box>
                  </Paper>

                  {/* Notes Section */}
                  <Paper elevation={1} sx={{ p: 2, borderRadius: '12px', border: `1px solid ${alpha("#1976d2", 0.2)}` }}>
                    <Stack direction="row" alignItems="center" spacing={2} mb={2}>
                      <Box sx={{ bgcolor: alpha("#1976d2", 0.15), p: 1, borderRadius: '10px' }}>
                        <NotesIcon sx={{ color: "#1976d2" }} />
                      </Box>
                      <Typography variant="subtitle1" fontWeight="bold" color="primary">
                        {t("notes") || "Internal Notes"}
                      </Typography>
                    </Stack>
                    <TextField
                      multiline
                      minRows={4}
                      value={notes}
                      onChange={e => {
                        if (e.target.value.length <= MAX_NOTES_LENGTH) {
                          setNotes(e.target.value);
                        }
                      }}
                      fullWidth
                      placeholder="Internal notes (max 500 characters)"
                    />
                    <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 2 }}>
                      <LinearProgress
                        variant="determinate"
                        value={(notesLength / MAX_NOTES_LENGTH) * 100}
                        sx={{ flexGrow: 1, height: 8, borderRadius: 4, bgcolor: alpha("#b0b6c7", 0.5) }}
                        color={notesLength >= MAX_NOTES_LENGTH ? "error" : "primary"}
                      />
                      <Typography
                        variant="caption"
                        color={notesLength >= MAX_NOTES_LENGTH ? "error" : "text.secondary"}
                        sx={{ fontWeight: 'medium', whiteSpace: 'nowrap' }}
                      >
                        {notesLength} / {MAX_NOTES_LENGTH}
                      </Typography>
                    </Box>
                  </Paper>
                </Stack>
              </Grid>

              {/* Right Column */}
              <Grid item xs={12} md={4}>
                <Card elevation={3} sx={{ borderRadius: '16px', background: alpha("#388e3c", 0.05), mb: 2 }}>
                  <CardHeader
                    title={
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Box sx={{ bgcolor: alpha("#388e3c", 0.15), p: 1.2, borderRadius: '12px', mr: 2 }}>
                          <ReceiptLongIcon sx={{ color: "#388e3c" }} fontSize="large" />
                        </Box>
                        <Typography variant="h6" fontWeight="bold" color="success.main">
                          {t("payment_settings") || "Payment Settings"}
                        </Typography>
                      </Box>
                    }
                    sx={{ background: alpha("#388e3c", 0.1), borderBottom: `1px solid ${alpha("#388e3c", 0.15)}` }}
                  />
                  <CardContent>
                    <Stack spacing={3}>
                      {/* Payment Method */}
                      <Paper elevation={1} sx={{ p: 2, borderRadius: '12px', border: `1px solid ${alpha("#388e3c", 0.3)}`, bgcolor: 'white' }}>
                        <Stack direction="row" alignItems="center" spacing={2} mb={2}>
                          <Box sx={{ bgcolor: alpha("#388e3c", 0.15), p: 1, borderRadius: '10px' }}>
                            <PaymentIcon sx={{ color: "#388e3c" }} />
                          </Box>
                          <Typography variant="subtitle1" fontWeight="bold" color="success.main">
                            {t("payment_method") || "Payment Method"}
                          </Typography>
                        </Stack>
                        <FormControl fullWidth>
                          <Select
                            value={paymentMethod}
                            onChange={(e) => setPaymentMethod(e.target.value)}
                            sx={{ borderRadius: '10px' }}
                          >
                            {PAYMENT_METHOD_OPTIONS.map((option) => (
                              <MenuItem key={option.value} value={option.value}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <Typography fontSize="1.2rem">{option.icon}</Typography>
                                  <Typography>{option.label}</Typography>
                                </Box>
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Paper>

                      {/* Payment Section */}
                      <Paper elevation={1} sx={{ p: 2, borderRadius: '12px', border: `1px solid ${alpha("#388e3c", 0.3)}`, bgcolor: 'white' }}>
                        <PaymentSection
                          totalIncl={offerTotal}
                          invoicePaidAmount={offerPaidAmount}
                          setInvoicePaidAmount={setOfferPaidAmount}
                        />
                      </Paper>

                      {/* Discount Section */}
                      <Paper elevation={1} sx={{ p: 2, borderRadius: '12px', border: `1px solid ${alpha("#1976d2", 0.3)}`, bgcolor: 'white' }}>
                        <Grid container spacing={2} alignItems="center">
                          <Grid item xs={12} sm={6}>
                            <TextField
                              select
                              label={t("discount_type") || "Discount Type"}
                              value={discountType}
                              onChange={e => setDiscountType(e.target.value)}
                              fullWidth
                              SelectProps={{ native: true }}
                              sx={{ borderRadius: '10px' }}
                            >
                              <option value="percentage">{t("percentage") || "Percentage"}</option>
                              <option value="fixed">{t("fixed_amount") || "Fixed Amount"}</option>
                            </TextField>
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <TextField
                              fullWidth
                              type="number"
                              label={t("discount_value") || "Discount Value"}
                              value={discountValue}
                              onChange={e => setDiscountValue(e.target.value)}
                              placeholder={discountType === "percentage" ? t("eg_10") || "e.g. 10" : t("eg_50") || "e.g. 50"}
                              sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px' } }}
                              InputProps={{
                                startAdornment: discountType === "percentage" ? null : <Box sx={{ mr: 1 }}>€</Box>,
                                endAdornment: discountType === "percentage" ? <Box sx={{ ml: 1 }}>%</Box> : null
                              }}
                            />
                          </Grid>
                        </Grid>
                      </Paper>

                      {/* Status Section */}
                      <Paper elevation={1} sx={{ p: 2, borderRadius: '12px', border: `1px solid ${alpha("#388e3c", 0.3)}`, bgcolor: 'white' }}>
                        <FormControl fullWidth>
                          <InputLabel>{t("offer_status") || "Offer Status"}</InputLabel>
                          <Select
                            value={offerStatus}
                            onChange={(e) => setOfferStatus(e.target.value)}
                            label={t("offer_status") || "Offer Status"}
                            sx={{ borderRadius: '10px' }}
                          >
                            {OFFER_STATUS_OPTIONS.map((option) => (
                              <MenuItem key={option.value} value={option.value}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <Box sx={{ width: 12, height: 12, borderRadius: '50%', backgroundColor: option.color }} />
                                  {option.label}
                                </Box>
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Paper>

                      {/* Total Summary */}
                      <Paper elevation={1} sx={{ p: 2, borderRadius: '12px', border: `1px solid ${alpha("#388e3c", 0.3)}`, bgcolor: alpha("#1976d2", 0.03) }}>
                        <Stack spacing={2}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', p: 1.5, bgcolor: 'white', borderRadius: '10px', border: `1px solid ${alpha("#b0b6c7", 0.2)}` }}>
                            <Typography variant="subtitle1">{t("subtotal_excl_vat") || "Subtotal (Excl. VAT)"}:</Typography>
                            <Typography variant="subtitle1" fontWeight="bold">€{totalExcl.toFixed(2)}</Typography>
                          </Box>
                          
                          {/* ✅ Show discount before VAT */}
                          {discountAmount > 0 && (
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', p: 1.5, bgcolor: alpha("#ff9800", 0.1), borderRadius: '10px', border: `1px solid ${alpha("#ff9800", 0.3)}` }}>
                              <Typography variant="subtitle1" color="warning.main">{t("discount")}:</Typography>
                              <Typography variant="subtitle1" fontWeight="bold" color="warning.main">-€{discountAmount.toFixed(2)}</Typography>
                            </Box>
                          )}
                          
                          {/* ✅ Show amount after discount */}
                          {discountAmount > 0 && (
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', p: 1.5, bgcolor: 'white', borderRadius: '10px', border: `1px solid ${alpha("#b0b6c7", 0.2)}` }}>
                              <Typography variant="subtitle1">{t("after_discount") || "After Discount"}:</Typography>
                              <Typography variant="subtitle1" fontWeight="bold">€{amountAfterDiscount.toFixed(2)}</Typography>
                            </Box>
                          )}
                          
                          {/* ✅ VAT calculated on discounted amount */}
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', p: 1.5, bgcolor: 'white', borderRadius: '10px', border: `1px solid ${alpha("#b0b6c7", 0.2)}` }}>
                            <Typography variant="subtitle1">{t("vat")}:</Typography>
                            <Typography variant="subtitle1" fontWeight="bold">€{totalVAT.toFixed(2)}</Typography>
                          </Box>
                          
                          {/* ✅ Final total */}
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', p: 2, bgcolor: alpha("#1976d2", 0.1), borderRadius: '10px', border: `1px solid ${alpha("#1976d2", 0.2)}` }}>
                            <Typography variant="h6" fontWeight="bold" color="primary">{t("offer_total") || "Total (Incl. VAT)"}:</Typography>
                            <Typography variant="h6" fontWeight="bold" color="primary">€{offerTotal.toFixed(2)}</Typography>
                          </Box>
                          
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', p: 1.5, bgcolor: 'white', borderRadius: '10px', border: `1px solid ${alpha("#4caf50", 0.3)}` }}>
                            <Typography variant="subtitle1" color="success.main">{t("paid_amount")}:</Typography>
                            <Typography variant="subtitle1" fontWeight="bold" color="success.main">€{paidAmount.toFixed(2)}</Typography>
                          </Box>
                          
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', p: 1.5, bgcolor: 'white', borderRadius: '10px', border: `1px solid ${alpha("#f44336", 0.3)}` }}>
                            <Typography variant="subtitle1" color="error.main">{t("due_amount")}:</Typography>
                            <Typography variant="subtitle1" fontWeight="bold" color="error.main">€{dueAmount.toFixed(2)}</Typography>
                          </Box>
                        </Stack>
                      </Paper>

                      {/* Action Buttons */}
                      <Box>
                        <Grid container spacing={2} direction="column" alignItems="stretch">
                          <Grid item xs={12}>
                            <Button
                              variant="outlined"
                              fullWidth
                              size="large"
                              startIcon={<PreviewIcon />}
                              onClick={handleGeneratePDF}
                              disabled={offerProducts.length === 0}
                              sx={{
                                py: 1.5,
                                borderRadius: '12px',
                                borderWidth: '2px',
                                borderColor: "#1976d2",
                                fontSize: '1rem',
                                textTransform: 'none',
                                fontWeight: 'bold',
                                mb: 2,
                                '&:hover': {
                                  borderWidth: '2px',
                                  backgroundColor: alpha("#1976d2", 0.04)
                                }
                              }}
                            >
                              {t("preview_offer") || "Preview Offer"}
                            </Button>
                          </Grid>
                          <Grid item xs={12}>
                            <Button
                              variant="contained"
                              fullWidth
                              size="large"
                              onClick={handleSubmit}
                              disabled={offerProducts.length === 0 || !selectedCustomer}
                              sx={{
                                py: 1.5,
                                borderRadius: '12px',
                                background: "linear-gradient(45deg, #388e3c 20%, #1976d2 90%)",
                                boxShadow: '0 8px 20px rgba(99, 102, 241, 0.1)',
                                fontSize: '1rem',
                                textTransform: 'none',
                                fontWeight: 'bold',
                                transition: 'all 0.3s',
                                '&:hover': {
                                  transform: 'translateY(-2px)',
                                  boxShadow: '0 10px 25px rgba(99, 102, 241, 0.2)'
                                }
                              }}
                            >
                              {t("create_offer")}
                            </Button>
                          </Grid>
                        </Grid>
                      </Box>
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Container>

      <UnsavedChangesDialog
        open={blocker.state === 'blocked'}
        onDiscard={() => blocker.proceed?.()}
        onCancel={() => blocker.reset?.()}
      />
    </Box>
  );
}

export default AddOfferPage;
